import React, { useState, useCallback, useRef } from 'react'; // Added useState, useRef
import { Routes, Route } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
// Added RefreshCw for regenerate icon
import { UploadCloud, CheckCircle, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { cn } from './lib/utils';
import { motion } from 'framer-motion';
import Auth from './components/Auth';
import LogoutButton from './components/LogoutButton';
import ProtectedRoute from './components/ProtectedRoute';

// Import Page Components
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Header from './components/Header';
// Import the new API service functions
import { uploadImage, generateImages, getGenerationHistory } from './services/api'; // Added getGenerationHistory

// Define the PhotoUploader component separately
const PhotoUploader = () => {
    // State for multiple files
    const [selectedFiles, setSelectedFiles] = useState([]); // Array of selected File objects
    const [previewUrls, setPreviewUrls] = useState([]); // Array of preview Data URLs
    // Updated state structure
    const [imageResults, setImageResults] = useState([]); // Array to store {id, originalFiles, originalDataUrls, generatedR2Keys: string[] | null, status, errorMessage}
    const [isGenerating, setIsGenerating] = useState(false); // Global flag if any generation is active
    const fileInputRef = useRef(null); // Ref for the file input

    // const WEBHOOK_URL = '...'; // No longer needed?

    const handleFileChange = useCallback((event) => {
        const files = event.target.files;
        if (!files || files.length === 0) {
            setSelectedFiles([]);
            setPreviewUrls([]);
            return;
        }

        const validFiles = [];
        const previewPromises = [];
        const maxFileSize = 10 * 1024 * 1024; // 10MB

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (!file.type.startsWith('image/')) {
                console.warn(`Invalid file type skipped: ${file.name}`);
                // TODO: Show user feedback about skipped files
                continue;
            }
            if (file.size > maxFileSize) {
                console.warn(`File size too large skipped: ${file.name}`);
                // TODO: Show user feedback about skipped files
                continue;
            }

            validFiles.push(file);

            // Create a promise to read the file as Data URL
            previewPromises.push(new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            }));
        }

        setSelectedFiles(validFiles);

        // Wait for all previews to load
        Promise.all(previewPromises)
            .then(urls => {
                setPreviewUrls(urls);
            })
            .catch(error => {
                console.error("Error generating previews:", error);
                // Handle preview errors if necessary
                setPreviewUrls([]); // Clear previews on error
            });

        // Reset the input value so the same files can be selected again if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }

    }, []); // Dependencies: none

    // Rewritten handleGenerate for multi-file upload
    const handleGenerate = useCallback(async () => {
        if (selectedFiles.length === 0 || isGenerating) return;

        const id = crypto.randomUUID(); // Unique ID for this generation batch
        const currentFiles = [...selectedFiles]; // Capture current files
        const currentPreviews = [...previewUrls]; // Capture current previews

        setIsGenerating(true);

        // --- Update State (Start) ---
        // Add a placeholder result immediately
        const newResultPlaceholder = {
            id,
            originalFiles: currentFiles,
            originalDataUrls: currentPreviews,
            generatedR2Keys: null, // Initialize generated keys as null
            status: 'generating',
            errorMessage: null,
        };
        // Prepend new result
        setImageResults(prev => [newResultPlaceholder, ...prev]);

        // Clear selection *after* capturing files/previews and adding placeholder
        setSelectedFiles([]);
        setPreviewUrls([]);
        if (fileInputRef.current) fileInputRef.current.value = null;

        // --- API Calls ---
        let success = false;
        let generatedKeys = null;
        let errorDetail = null;

        try {
            // 1. Upload all selected images concurrently
            const uploadPromises = currentFiles.map(file => uploadImage(file));
            const uploadedR2Keys = await Promise.all(uploadPromises);
            console.log("Uploaded R2 Keys:", uploadedR2Keys); // For debugging

            // 2. Call generate endpoint with the keys
            const generationResponse = await generateImages(uploadedR2Keys);
            console.log("Generation Response:", generationResponse); // For debugging

            if (generationResponse && generationResponse.generatedImageUrls) {
                 // The backend returns R2 keys in the 'generatedImageUrls' field based on our handler
                generatedKeys = generationResponse.generatedImageUrls;
                success = true;
            } else {
                throw new Error('Generation response did not contain expected generated image keys.');
            }

        } catch (error) {
            console.error("Image generation process error:", error);
            errorDetail = error.message || 'An error occurred during the generation process.';
            success = false;
        }

        // --- Update State (Finish) ---
        // Update the specific result placeholder by ID
        setImageResults(prev => prev.map(r =>
            r.id === id
                ? { ...r, status: success ? 'success' : 'error', generatedR2Keys: generatedKeys, errorMessage: errorDetail }
                : r
        ));

        setIsGenerating(false); // Clear global flag

    }, [selectedFiles, previewUrls, isGenerating]); // Dependencies

    // Function to handle regeneration for a specific result
    const handleRegenerate = useCallback(async (resultId, originalFiles) => {
        if (!originalFiles || originalFiles.length === 0 || isGenerating) return;

        setIsGenerating(true);

        // --- Update State (Start) ---
        // Find the result and set its status to 'generating', clearing old keys/error
        setImageResults(prev => prev.map(r =>
            r.id === resultId
                ? { ...r, status: 'generating', generatedR2Keys: null, errorMessage: null }
                : r
        ));

        // --- API Calls ---
        let success = false;
        let generatedKeys = null;
        let errorDetail = null;

        try {
            // 1. Re-upload the original images concurrently
            const uploadPromises = originalFiles.map(file => uploadImage(file));
            const uploadedR2Keys = await Promise.all(uploadPromises);
            console.log("Re-uploaded R2 Keys:", uploadedR2Keys); // For debugging

            // 2. Call generate endpoint with the new keys
            const generationResponse = await generateImages(uploadedR2Keys);
            console.log("Regeneration Response:", generationResponse); // For debugging

            if (generationResponse && generationResponse.generatedImageUrls) {
                generatedKeys = generationResponse.generatedImageUrls; // These are the new R2 keys
                success = true;
            } else {
                throw new Error('Regeneration response did not contain expected generated image keys.');
            }

        } catch (error) {
            console.error("Image regeneration process error:", error);
            errorDetail = error.message || 'An error occurred during the regeneration process.';
            success = false;
        }

        // --- Update State (Finish) ---
        // Update the specific result by ID with new keys/status
        setImageResults(prev => prev.map(r =>
            r.id === resultId
                ? { ...r, status: success ? 'success' : 'error', generatedR2Keys: generatedKeys, errorMessage: errorDetail }
                : r
        ));

        setIsGenerating(false); // Clear global flag

    }, [isGenerating]); // Dependencies: isGenerating (to prevent concurrent runs)


    return (
        // Adjusted layout for results display
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black p-4 sm:p-8 flex flex-col items-center relative">
            {/* Uploader Section */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="w-full max-w-lg bg-white/5 backdrop-blur-md rounded-xl shadow-2xl border border-white/10 p-6 space-y-6 mb-8" // Increased max-width
            >
                <h1 className="text-3xl font-bold text-center text-white">
                    <UploadCloud className="inline-block w-8 h-8 mr-2" />
                    Photo Uploader
                </h1>

                <div className="space-y-4">
                    <Input
                        ref={fileInputRef} // Added ref
                        type="file"
                        id="file-input"
                        onChange={handleFileChange}
                        className={cn(
                            "w-full",
                            "file:bg-gradient-to-r file:from-purple-500 file:to-blue-500",
                            "file:text-white file:px-4 file:py-2 file:rounded-md file:border-none",
                            "file:shadow-md file:cursor-pointer",
                            "file:transition-all file:duration-300 file:hover:scale-105",
                            "bg-black/20 text-white border border-white/10",
                            "placeholder:text-gray-400",
                            // uploadStatus === 'error' && 'border-red-500 text-red-400', // Error state removed from input, handled per result
                            "focus:outline-none focus:ring-2 focus:ring-purple-500/50",
                            "h-12", // Increased height
                            isGenerating && "opacity-50 cursor-not-allowed" // Disable style
                        )}
                        accept="image/*"
                        multiple // Allow multiple file selection
                        disabled={isGenerating} // Disable input while generating
                    />
                     <label htmlFor="file-input" className="block text-sm text-gray-300">
                        Choose photo(s) (max 10MB each)
                    </label>

                    {/* Preview Area for Multiple Files */}
                    {previewUrls.length > 0 && (
                         <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4">
                             <p className="text-sm text-gray-400 mb-2 text-center">Selected Previews:</p>
                             <div className="flex flex-wrap justify-center gap-2">
                                {previewUrls.map((url, index) => (
                                    <img
                                        key={index}
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        className="w-20 h-20 object-cover rounded-md border border-white/20 shadow-sm" // Small thumbnails
                                    />
                                ))}
                             </div>
                        </motion.div>
                    )}

                    <Button
                        onClick={handleGenerate} // Call the updated handler (no args needed)
                        disabled={selectedFiles.length === 0 || isGenerating} // Disable if no files selected or generating
                        className={cn(
                            "w-full py-3 text-white font-semibold rounded-lg",
                            "bg-gradient-to-r from-purple-500 to-blue-500",
                            "hover:from-purple-600 hover:to-blue-600",
                            "transition-all duration-300",
                            "shadow-lg hover:shadow-xl",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            "flex items-center justify-center gap-2",
                            isGenerating && 'animate-pulse'
                        )}
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            `Generate Image${selectedFiles.length > 1 ? 's' : ''}` // Dynamic button text
                        )}
                    </Button>
                </div>
            </motion.div>

            {/* Results Section */}
            {imageResults.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="w-full max-w-4xl space-y-6" // Wider container for results
                >
                    <h2 className="text-2xl font-semibold text-white text-center mb-4">Results</h2>
                    {imageResults.map((result) => (
                        <motion.div
                            key={result.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white/10 backdrop-blur-sm rounded-lg shadow-lg border border-white/15 p-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-center"
                        >
                            {/* Original Images */}
                            <div className="text-center">
                                <h3 className="text-lg font-medium text-gray-300 mb-2">Original(s)</h3>
                                <div className="flex flex-wrap justify-center gap-2">
                                    {result.originalDataUrls.map((url, index) => (
                                        <img
                                            key={index}
                                            src={url}
                                            alt={`Original Upload ${index + 1}`}
                                            className="w-24 h-24 object-cover rounded border border-white/10 shadow-sm" // Slightly larger thumbnails for results
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Generated Image / Status */}
                            <div className="text-center flex flex-col items-center justify-center space-y-2">
                                <h3 className="text-lg font-medium text-gray-300 mb-2">Generated</h3>
                                {result.status === 'generating' && (
                                    <div className="flex flex-col items-center text-blue-400">
                                        <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                        <span>Generating...</span>
                                    </div>
                                )}
                                {result.status === 'error' && (
                                     <div className="bg-red-500/20 border border-red-500/30 text-red-400 p-3 rounded-md text-sm w-full">
                                        <AlertTriangle className="w-5 h-5 inline mr-1" />
                                        {result.errorMessage || 'Generation failed.'}
                                    </div>
                                )}
                                {result.status === 'success' && result.generatedR2Keys && result.generatedR2Keys.length > 0 && (
                                    // Map over the generated R2 keys and display each image via the proxy
                                    <div className="flex flex-wrap justify-center gap-2">
                                        {/* generatedR2Keys now contains signed GET URLs */}
                                        {result.generatedR2Keys.map((signedUrl, index) => (
                                            <img
                                                key={index}
                                                // Use the signed URL directly
                                                src={signedUrl}
                                                alt={`Generated Content ${index + 1}`}
                                                className="max-w-full h-auto rounded border border-white/10 shadow-sm mx-auto" // Adjust styling as needed for multiple images
                                            />
                                        ))}
                                    </div>
                                )}
                                {/* Add Regenerate button back */}
                                {(result.status === 'success' || result.status === 'error') && (
                                    <Button
                                        onClick={() => handleRegenerate(result.id, result.originalFiles)} // Call handleRegenerate with id and originalFiles
                                        disabled={isGenerating} // Disable if any generation is active
                                        size="sm"
                                        variant="outline"
                                        className="mt-3 bg-black/20 border-white/20 hover:bg-white/20 text-white"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Regenerate
                                    </Button>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            )}
        </div>
    );
};

// Define the main App component for routing (Remains the same)
function App() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <PhotoUploader />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

export default App;
